// src/pages/VendorApp.js
// Vendor dashboard — vendor tabs only, no mall, no super admin
// Shown after vendor login

import React, { useState } from 'react';
import { useAuth }      from '../context/AuthContext';
import { useLanguage }  from '../context/LanguageContext';
import LangToggle       from '../components/LangToggle';
import KioskPage        from './KioskPage';
import KitchenDisplay   from './KitchenDisplay';
import OrdersPage       from './OrdersPage';
import BillingPage      from './BillingPage';
import PromoPage        from './PromoPage';
import AnalyticsPage    from './AnalyticsPage';
import BillingPlanPage  from './BillingPlanPage';
import VendorPage       from './VendorPage';
import OnboardingPanel  from '../components/OnboardingPanel';
import StaffManager     from '../components/StaffManager';
import usePWA             from '../hooks/usePWA';
import { VendorQRGenerator } from '../components/QRCodeGenerator';
import TestModeBanner    from '../components/TestModeBanner';
import {
  DEPLOYMENT_MODES,
  DEPLOYMENT_MODE_LABELS,
} from '../constants/deploymentModes';

const STAFF_PIN = process.env.REACT_APP_STAFF_PIN || '1234';

// Tabs visible per deployment mode
const VENDOR_TABS = {
  STANDALONE_FULL: [
    { id:'kiosk',        label:'🛒 Kiosk'        },
    { id:'orders',       label:'📋 Orders'       },
    { id:'kitchen',      label:'👨‍🍳 Kitchen'    },
    { id:'billing',      label:'💳 Billing'      },
    { id:'promos',       label:'🎟 Promos'       },
    { id:'analytics',    label:'📊 Analytics'    },
    { id:'subscription', label:'💳 Subscription' },
    { id:'qr',           label:'📱 QR Code'      },
    { id:'admin',        label:'🍽 Admin'        },
  ],
  STANDALONE_POS_BRIDGE: [
    { id:'kiosk',        label:'🛒 Kiosk'        },
    { id:'orders',       label:'📋 Orders'       },
    { id:'billing',      label:'💳 Billing'      },
    { id:'analytics',    label:'📊 Analytics'    },
    { id:'subscription', label:'💳 Subscription' },
    { id:'qr',           label:'📱 QR Code'      },
    { id:'admin',        label:'🍽 Admin'        },
  ],
  MALL_MANAGED: [
    { id:'orders',       label:'📋 Orders'       },
    { id:'kitchen',      label:'👨‍🍳 Kitchen'    },
    { id:'analytics',    label:'📊 Analytics'    },
    { id:'admin',        label:'🍽 Admin'        },
  ],
  MALL_PLUS_STALL: [
    { id:'kiosk',        label:'🛒 Kiosk'        },
    { id:'orders',       label:'📋 Orders'       },
    { id:'kitchen',      label:'👨‍🍳 Kitchen'    },
    { id:'billing',      label:'💳 Billing'      },
    { id:'promos',       label:'🎟 Promos'       },
    { id:'analytics',    label:'📊 Analytics'    },
    { id:'subscription', label:'💳 Subscription' },
    { id:'qr',           label:'📱 QR Code'      },
    { id:'admin',        label:'🍽 Admin'        },
  ],
  TABLE_QR_ONLY: [
    { id:'orders',       label:'📋 Orders'       },
    { id:'kitchen',      label:'👨‍🍳 Kitchen'    },
    { id:'analytics',    label:'📊 Analytics'    },
    { id:'subscription', label:'💳 Subscription' },
    { id:'admin',        label:'🍽 Admin'        },
  ],
};

function PinModal({ onSuccess, onCancel }) {
  const [pin, setPin] = useState('');
  const [err, setErr] = useState('');
  const submit = () => {
    if (pin === STAFF_PIN) onSuccess();
    else { setErr('Incorrect PIN'); setPin(''); }
  };
  return (
    <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.85)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:9999 }}>
      <div style={{ background:'var(--bg2)', border:'1px solid var(--border)', borderRadius:16, padding:40, width:320, textAlign:'center' }}>
        <div style={{ fontSize:32, marginBottom:12 }}>🔒</div>
        <div style={{ fontWeight:700, fontSize:18, marginBottom:6 }}>Staff Access</div>
        <div style={{ fontSize:13, color:'var(--text2)', marginBottom:24 }}>Enter PIN to exit customer mode</div>
        <input type="password" value={pin}
          onChange={e => { setPin(e.target.value); setErr(''); }}
          onKeyDown={e => e.key==='Enter' && submit()}
          placeholder="Enter PIN" autoFocus
          style={{ textAlign:'center', fontSize:22, letterSpacing:8, marginBottom:10 }} />
        {err && <div style={{ color:'var(--red)', fontSize:13, marginBottom:10 }}>{err}</div>}
        <div style={{ display:'flex', gap:8, marginTop:8 }}>
          <button className="btn btn-ghost" style={{ flex:1 }} onClick={onCancel}>Cancel</button>
          <button className="btn btn-primary" style={{ flex:1 }} onClick={submit}>Unlock</button>
        </div>
        <div style={{ fontSize:11, color:'var(--text3)', marginTop:14 }}>Default PIN: 1234</div>
      </div>
    </div>
  );
}

export default function VendorApp() {
  const { vendor, logout }     = useAuth();
  const { t, isAr }            = useLanguage();
  const notLive = !!vendor?.status && vendor.status !== 'ACTIVE';
  const [tab,         setTab]  = useState(notLive ? 'onboarding' : 'orders');
  const [kioskMode,   setKiosk]= useState(false);
  const [showPin,  setShowPin] = useState(false);
  const { isOnline, isInstallable, installApp } = usePWA();

  const mode  = vendor?.deploymentMode || DEPLOYMENT_MODES.STANDALONE_FULL;
  const baseTabs = VENDOR_TABS[mode] || VENDOR_TABS.STANDALONE_FULL;
  const withOnboarding = notLive ? [{ id: 'onboarding', label: '🚀 Onboarding' }, ...baseTabs] : baseTabs;
  const tabs  = [...withOnboarding, { id: 'staff', label: '👥 Staff' }];
  const hasKiosk = tabs.some(t => t.id === 'kiosk');

  // Full-screen kitchen display
  if (tab === 'kitchen') return (
    <div>
      <div style={{ position:'fixed', top:0, right:0, zIndex:999, padding:'6px 10px' }}>
        <button onClick={() => setTab('orders')}
          style={{ background:'rgba(0,0,0,0.5)', color:'#666', border:'1px solid #333', borderRadius:6, fontSize:11, padding:'4px 10px', cursor:'pointer' }}>
          ✕ Exit KDS
        </button>
      </div>
      <KitchenDisplay />
    </div>
  );

  // Customer-facing kiosk mode
  if (kioskMode) return (
    <div style={{ position:'relative' }}>
      {showPin && (
        <PinModal
          onSuccess={() => { setKiosk(false); setShowPin(false); }}
          onCancel={() => setShowPin(false)}
        />
      )}
      {/* Always-visible staff access button - no fullscreen, no escape key */}
      <div style={{ position:'fixed', bottom:16, right:16, zIndex:9999 }}>
        <button onClick={() => setShowPin(true)}
          style={{ background:'rgba(15,15,15,0.9)', color:'#555', border:'1px solid #333',
            borderRadius:8, fontSize:11, padding:'8px 14px', cursor:'pointer',
            backdropFilter:'blur(4px)' }}>
          🔒 Staff Access
        </button>
      </div>
      <KioskPage vendorId={vendor?.id} kioskModeEnabled={true} vendorName={vendor?.name} kioskTheme={vendor?.kioskTheme} brandColor={vendor?.brandColor} bgColor={vendor?.bgColor} tileColor={vendor?.tileColor} fontColor={vendor?.fontColor} buttonTextColor={vendor?.buttonTextColor} surfaceColor={vendor?.surfaceColor} serviceFeeConfig={vendor?.serviceFeeConfig} />
    </div>
  );

  return (
    <div>
      <TestModeBanner />
      {notLive && (
        <div style={{ background:'#3d2e10', color:'var(--accent)', padding:'10px 16px', fontSize:13, fontWeight:600 }}>
          ⚠ Your account is not yet live on the kiosk. Complete the steps in the Onboarding tab to go live.
        </div>
      )}
      {/* Vendor nav */}
      <div className="nav" style={{ flexWrap:'wrap', gap:6, direction: isAr ? 'rtl' : 'ltr' }}>
        <div style={{ display:'flex', alignItems:'center', gap:10 }}>
          <div className="nav-logo">⚡ QuickServe</div>
          {!isOnline && <span style={{ fontSize:11, padding:'3px 8px', borderRadius:20, background:'#3d1a1a', color:'var(--red)' }}>Offline</span>}
        </div>

        {/* Only vendor tabs */}
        <div className="nav-tabs" style={{ flexWrap:'wrap' }}>
          {tabs.map(tb => (
            <button key={tb.id}
              className={`nav-tab${tab===tb.id?' active':''}`}
              onClick={() => setTab(tb.id)}>
              {tb.label}
            </button>
          ))}
        </div>

        <div style={{ display:'flex', alignItems:'center', gap:8 }}>
          <LangToggle />
          {/* Customer mode button — only for modes with a kiosk */}
          {hasKiosk && tab === 'kiosk' && (
            <button className="btn btn-ghost btn-sm"
              onClick={() => setKiosk(true)}
              style={{ borderColor:'var(--accent)', color:'var(--accent)' }}>
              👤 Customer Mode
            </button>
          )}
          {isInstallable && <button className="btn btn-ghost btn-sm" onClick={installApp}>📲 Install</button>}
          <div style={{ fontSize:12, color:'var(--text2)' }}>{vendor?.emoji} {vendor?.name}</div>
          <div style={{ fontSize:10, padding:'2px 8px', borderRadius:10, background:'var(--bg3)', color:'var(--text3)' }}>
            {DEPLOYMENT_MODE_LABELS[mode]?.split('—')[0]?.trim()}
          </div>
          <button className="btn btn-ghost btn-sm" onClick={logout}>Logout</button>
        </div>
      </div>

      {/* Tab content */}
      {tab === 'kiosk'     && <KioskPage vendorId={vendor?.id} kioskModeEnabled={false} vendorName={vendor?.name} kioskTheme={vendor?.kioskTheme} brandColor={vendor?.brandColor} bgColor={vendor?.bgColor} tileColor={vendor?.tileColor} fontColor={vendor?.fontColor} buttonTextColor={vendor?.buttonTextColor} surfaceColor={vendor?.surfaceColor} serviceFeeConfig={vendor?.serviceFeeConfig} />}
      {tab === 'orders'    && <OrdersPage />}
      {tab === 'billing'   && <BillingPage />}
      {tab === 'promos'    && <PromoPage />}
      {tab === 'analytics'    && <AnalyticsPage />}
      {tab === 'subscription' && <BillingPlanPage />}
      {tab === 'qr'           && (
        <div className="page">
          <div className="page-title" style={{ marginBottom:4 }}>📱 QR Code</div>
          <div className="page-sub" style={{ marginBottom:24 }}>Let customers order from their phone by scanning a QR code</div>
          <div className="card" style={{ maxWidth:480 }}>
            <VendorQRGenerator vendor={vendor} />
          </div>
        </div>
      )}
      {tab === 'admin'     && <VendorPage />}
      {tab === 'onboarding' && <OnboardingPanel />}
      {tab === 'staff'      && <StaffManager />}
    </div>
  );
}
